import React, { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import Dropzone from "./Dropzone";
import ImageThumbReorderGrid from "./ImageThumbReorderGrid";

function baseName(filename = "image.jpg") {
  return filename.replace(/\.[^/.]+$/, "");
}

export default function ImagesToPdf() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { url, name }

  const canConvert = useMemo(
    () => files.length > 0 && !loading,
    [files, loading]
  );

  const cleanup = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
  };

  // ✅ APPEND + anti-doublons
  const onPick = (picked) => {
    cleanup();

    const onlyImages = (picked || []).filter((f) =>
      f.type?.startsWith("image/")
    );
    if (!onlyImages.length) return;

    setFiles((prev) => {
      const key = (f) => `${f.name}__${f.size}__${f.lastModified}`;
      const existing = new Set(prev.map(key));
      const toAdd = onlyImages.filter((f) => !existing.has(key(f)));
      return [...prev, ...toAdd];
    });
  };

  const onReorder = (from, to) => {
    cleanup();
    setFiles((prev) => {
      const next = [...prev];
      if (to < 0 || to >= next.length) return prev;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const onRemove = (index) => {
    cleanup();
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ Mode "contain" : A4 standard, pas de zoom excessif, centrage
  const convert = async () => {
    if (!files.length) return;

    setLoading(true);
    cleanup();

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const imgUrl = URL.createObjectURL(file);

        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = imgUrl;
        });

        const imgRatio = img.width / img.height;
        const pageRatio = pageW / pageH;

        let drawW, drawH;

        // ✅ CONTAIN : l'image rentre dans la page sans dépasser
        if (imgRatio > pageRatio) {
          // image plus large que la page
          drawW = pageW;
          drawH = drawW / imgRatio;
        } else {
          // image plus haute que la page
          drawH = pageH;
          drawW = drawH * imgRatio;
        }

        const x = (pageW - drawW) / 2;
        const y = (pageH - drawH) / 2;

        if (i > 0) pdf.addPage();

        // jsPDF + Image: on met en JPEG pour compat max
        pdf.addImage(img, "JPEG", x, y, drawW, drawH, undefined, "FAST");

        URL.revokeObjectURL(imgUrl);
      }

      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);

      const name =
        files.length === 1
          ? `${baseName(files[0].name)}-convert.pdf`
          : `${baseName(files[0].name)}-convert-${files.length}-images.pdf`;

      setResult({ url, name });
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la conversion images → PDF.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    cleanup();
    setFiles([]);
  };

  return (
    <section style={{ padding: 16 }}>
      <Dropzone
        accept="image/*"
        multiple
        disabled={loading}
        acceptText="Déposez vos images ici"
        helperText="Glissez-déposez plusieurs images ou cliquez sur “Choisir un fichier”"
        onFiles={onPick}
      />

      {files.length > 0 && (
        <>
          <p style={{ textAlign: "center", color: "#666", marginTop: 8 }}>
            <b>{files.length}</b> image(s) — glisse pour changer l’ordre.
          </p>

          <ImageThumbReorderGrid
            files={files}
            onReorder={onReorder}
            onRemove={onRemove}
          />
        </>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          marginTop: 12,
          flexWrap: "wrap",
        }}
      >
        <button
          onClick={convert}
          disabled={!canConvert}
          style={{ minWidth: 220 }}
        >
          {loading ? "Conversion..." : "Convertir en PDF"}
        </button>

        {files.length > 0 && (
          <button onClick={reset} disabled={loading} style={{ minWidth: 160 }}>
            Réinitialiser
          </button>
        )}
      </div>

      {result?.url && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <a href={result.url} download={result.name}>
            Télécharger le PDF
          </a>
        </div>
      )}
    </section>
  );
}
